'use client';

/* eslint-disable no-restricted-imports */
import { useRouter } from 'next/navigation';

interface NavigateOptions {
  scroll?: boolean;
}

enum PrefetchKind {
  AUTO = 'auto',
  FULL = 'full',
  TEMPORARY = 'temporary',
}
interface PrefetchOptions {
  kind: PrefetchKind;
}

type AppRouterInstance = {
  /**
   * Navigate to the previous history entry.
   */
  back(): void;
  /**
   * Navigate to the next history entry.
   */
  forward(): void;
  /**
   * Refresh the current page.
   */
  refresh(): void;
  /**
   * Navigate to the provided href.
   * Pushes a new history entry.
   */
  push(href: string, options?: NavigateOptions): void;
  /**
   * Navigate to the provided href.
   * Replaces the current history entry.
   */
  replace(href: string, options?: NavigateOptions): void;
  /**
   * Prefetch the provided href.
   */
  prefetch(href: string, options?: PrefetchOptions): void;
};

/**
 * https://github.com/vercel/next.js/issues/65335#issuecomment-2583401824
 * `useSafeRouter` is a drop‑in replacement for `useSafeRouter` that applies the
 * same navigation guard used by `<SafeLink />`.
 *
 * ```tsx
 * const router = useSafeRouter();
 * router.push('/about-us/');  // internal route - uses Next.js router
 * router.push('/notice/');    // external route - uses window.location with properly prefixed URL
 * ```
 */
export const useSafeRouter = (): AppRouterInstance => {
  const router = useRouter();

  return {
    ...router,
    /**
     *
     * @param href https://github.com/vercel/next.js/issues/65335#issuecomment-2583401824
     * @returns
     */
    push: (href) => {
      router.push(href);
    },
    /**
     * https://github.com/vercel/next.js/issues/65335#issuecomment-2583401824
     * @param href
     * @returns
     */
    replace: (href) => {
      router.replace(href);
    },
  } satisfies AppRouterInstance;
};
